import { memo, type ReactNode } from 'react'
import { AlarmClock, Briefcase, CheckCircle2, CircleDashed, UserCheck } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import type { KpiFilter } from '../types'
import type { KpiCounts } from '../utils/job-status'

interface KpiCardProps {
  filter: KpiFilter
  label: string
  value: number
  /** The value depends on assignments that are still loading. */
  loading?: boolean
  hint?: string
  icon: ReactNode
  iconClassName: string
  active: boolean
  onToggle: (filter: KpiFilter) => void
  badge?: ReactNode
}

const KpiCard = memo(function KpiCard({ filter, label, value, loading = false, hint, icon, iconClassName, active, onToggle, badge }: KpiCardProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={`${label}: ${loading ? 'loading' : value}${hint ? ` ${hint}` : ''}. ${active ? 'Filter active, press to clear' : 'Press to filter'}`}
      onClick={() => onToggle(filter)}
      className={cn(
        'group relative flex w-full items-center gap-3 rounded-xl border bg-white p-3 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62] focus-visible:ring-offset-2',
        active
          ? 'border-[#169b62] bg-[#f3faf6] shadow-[inset_0_0_0_1px_#169b62]'
          : 'border-[#dfeae3] hover:border-[#b9d9c7] hover:bg-[#fafdfb]',
      )}
    >
      <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', iconClassName)} aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          {loading
            ? <Skeleton className="h-[26px] w-10" aria-hidden="true" />
            : <span className="text-[26px] font-semibold leading-none tabular-nums text-[#20352b]">{value}</span>}
          {hint ? <span className="truncate text-[11px] text-[#718078]">{hint}</span> : null}
        </span>
        <span className="mt-1 block text-[12px] font-medium text-[#4e6157]">{label}</span>
      </span>
      {loading ? null : badge}
      {active ? (
        <span className="absolute right-2 top-2 rounded-full bg-[#169b62] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
          Filter
        </span>
      ) : null}
    </button>
  )
})

export const KpiCards = memo(function KpiCards({
  counts,
  staffingLoading = false,
  activeFilter,
  onToggle,
}: {
  counts: KpiCounts | null
  /** Assignments still loading: every figure except the worker total shows a placeholder. */
  staffingLoading?: boolean
  activeFilter: KpiFilter | null
  onToggle: (filter: KpiFilter) => void
}) {
  if (!counts) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-busy="true" aria-label="Loading key figures">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-[74px] w-full rounded-xl" />
        ))}
      </div>
    )
  }

  const late = counts.runningLate

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" role="group" aria-label="Key figures — select one to filter the board">
      <KpiCard
        filter="on_job"
        label="Workers on job"
        value={counts.workersOnJob}
        loading={staffingLoading}
        hint={`of ${counts.totalWorkers} total`}
        icon={<Briefcase className="h-4 w-4" />}
        iconClassName="bg-blue-50 text-blue-700"
        active={activeFilter === 'on_job'}
        onToggle={onToggle}
      />
      <KpiCard
        filter="available"
        label="Available now"
        value={counts.availableNow}
        loading={staffingLoading}
        icon={<UserCheck className="h-4 w-4" />}
        iconClassName="bg-emerald-50 text-emerald-700"
        active={activeFilter === 'available'}
        onToggle={onToggle}
      />
      <KpiCard
        filter="unassigned"
        label="Jobs unassigned"
        value={counts.jobsUnassigned}
        loading={staffingLoading}
        icon={<CircleDashed className="h-4 w-4" />}
        iconClassName="bg-amber-50 text-amber-700"
        active={activeFilter === 'unassigned'}
        onToggle={onToggle}
      />
      <KpiCard
        filter="late"
        label="Running late"
        value={late}
        loading={staffingLoading}
        icon={late === 0 ? <CheckCircle2 className="h-4 w-4" /> : <AlarmClock className="h-4 w-4" />}
        iconClassName={late === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}
        active={activeFilter === 'late'}
        onToggle={onToggle}
        badge={late === 0 ? (
          <span className="text-[11px] font-medium text-emerald-700">All on time</span>
        ) : (
          <span className="relative flex h-3 w-3" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-70 motion-safe:animate-ping" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
          </span>
        )}
      />
    </div>
  )
})
